'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useURLParams } from '@/lib/navigation/useURLParams'
import { useSearchParam } from '@/lib/next/useSearchParam'
import { Accordion } from '@/ui/accordion/Accordion'
import { Drawer, DrawerAction, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import type { ProductListFilterFragment } from '@gql-generated/graphql'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import {
  isProductListPageLoadingAtom,
  productListFiltersMenuRefAtom,
  productListFiltersMobileItemOpen,
} from '../../services/productList.store'
import {
  createProductsFiltersDrawerClearFiltersIntent,
  createProductsFiltersDrawerCloseIntent,
} from '../../services/productListFilters.intents'
import { ProductListFiltersProduct } from '../product-list-filters-product/ProductListFiltersProduct.client'
import { ProductListFiltersMobileCategories } from './ProductListFiltersMobileCategories.client'
import { ProductListFiltersMobileSubcategories } from './ProductListFiltersMobileSubcategories.client'

export const CATEGORIES_FILTER_NAME = 'categories'
export const SUBCATEGORIES_FILTER_NAME = 'subcategories'

export type ProductListFiltersMobileDrawerProps = {
  categoryFilters?: ProductListFilterFragment[] | null
  subcategoriesFilters?: ProductListFilterFragment[] | null
  productFilters?: ProductListFilterFragment[] | null
  hasActiveFilters?: boolean
  isDefaultOpen?: boolean
}

export function ProductListFiltersMobileDrawer({
  categoryFilters,
  subcategoriesFilters,
  productFilters,
  hasActiveFilters,
  isDefaultOpen,
}: ProductListFiltersMobileDrawerProps) {
  const { t } = useTranslation(['productListFilters'])
  const [filterNameActive, setFilterNameActive] = useAtom(productListFiltersMobileItemOpen)
  const setIsProductListPageLoading = useSetAtom(isProductListPageLoadingAtom)
  const menuRef = useAtomValue(productListFiltersMenuRefAtom)

  const selectedFilterGroup = useSearchParam('selectedFilterGroup')

  const { setURLParams, removeURLParams } = useURLParams()

  const categoryFilterRef = useRef<HTMLDivElement>(null)
  const subcategoriesFilterRef = useRef<HTMLDivElement>(null)
  const filterRefsMap = useRef<Record<string, HTMLDivElement | null>>({})

  const resetMenuRefScroll = () => {
    menuRef?.current?.scrollTo(0, 0)
  }

  const closeDrawerIntent = useMemo(() => createProductsFiltersDrawerCloseIntent(), [])
  const clearFiltersIntent = useMemo(() => createProductsFiltersDrawerClearFiltersIntent(), [])

  useEffect(() => {
    if (isDefaultOpen) {
      setFilterNameActive(selectedFilterGroup || true)
    }
  }, [setFilterNameActive, selectedFilterGroup, isDefaultOpen])

  const handleCloseDrawer = () => {
    resetMenuRefScroll()
    setFilterNameActive(undefined)
  }

  const handleReset = () => {
    setURLParams([{ name: 'p', value: '1' }])
    removeURLParams([{ name: 'filter' }])
    setIsProductListPageLoading(true)
    resetMenuRefScroll()
  }

  useEffect(() => {
    if (filterNameActive) {
      setTimeout(() => {
        if (filterNameActive === SUBCATEGORIES_FILTER_NAME) {
          subcategoriesFilterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else if (filterNameActive === CATEGORIES_FILTER_NAME) {
          categoryFilterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else if (
          typeof filterNameActive === 'string' &&
          filterRefsMap.current[filterNameActive]
        ) {
          filterRefsMap.current[filterNameActive]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }, 100)
    }
  }, [filterNameActive])

  const setFilterRef = (title: string) => (element: HTMLDivElement | null) => {
    filterRefsMap.current[title] = element
  }

  if (!productFilters?.length) {
    return null
  }

  return (
    <Drawer
      isOpen={!!filterNameActive}
      onOpenChange={(open) => {
        if (!open) {
          handleCloseDrawer()
        }
      }}
      title={t('productListFilters:filters')}
      size="lg"
      className="[&_[data-scroll='none']]:!overflow-y-auto"
    >
      <DrawerBody>
        <Accordion className="w-full gap-1">
          {subcategoriesFilters && (
            <ProductListFiltersMobileSubcategories
              ref={subcategoriesFilterRef}
              subCategories={subcategoriesFilters}
              onClose={handleCloseDrawer}
              closeIntent={closeDrawerIntent}
            />
          )}

          {categoryFilters && (
            <ProductListFiltersMobileCategories
              ref={categoryFilterRef}
              categoryFilters={categoryFilters}
            />
          )}

          {productFilters?.map((productFilter) => (
            <ProductListFiltersProduct
              key={productFilter.title}
              ref={setFilterRef(productFilter.title)}
              productFilters={productFilter}
              isMobile
            />
          ))}
        </Accordion>
      </DrawerBody>

      <DrawerFooter>
        <DrawerAction
          type="custom"
          handle="reset-filters"
          variant="secondary"
          action={async (): Promise<false> => {
            handleReset()

            return false
          }}
          isDisabled={!hasActiveFilters}
          intent={clearFiltersIntent}
        >
          {t('productListFilters:reset')}
        </DrawerAction>
        <DrawerAction
          type="custom"
          handle="close-filters"
          variant="primary"
          action={handleCloseDrawer}
          intent={closeDrawerIntent}
        >
          {t('productListFilters:validate')}
        </DrawerAction>
      </DrawerFooter>
    </Drawer>
  )
}
